// Posts a lead snapshot to a configured webhook (Zapier "Catch Hook" URL).
//
// The webhook URL is read from process.env.REACT_APP_LEAD_WEBHOOK_URL.
// When the env var is empty (e.g. local dev / before the Zap is set up) the
// function silently no-ops so PDF generation is never blocked.
//
// Payload shape is intentionally flat so it maps cleanly to Google Sheet columns
// and Salesforce lead fields.

import { CURRENCIES } from "./calculations";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
];

export function readUtmParams() {
  if (typeof window === "undefined") return {};
  try {
    const params = new URLSearchParams(window.location.search);
    return UTM_KEYS.reduce((acc, k) => {
      const v = params.get(k);
      if (v) acc[k] = v;
      return acc;
    }, {});
  } catch {
    return {};
  }
}

function buildPayload({ state, results, currencyCode, executiveSummary, utm }) {
  const symbol = CURRENCIES[currencyCode]?.symbol || "";

  return {
    // --- Lead identity ----------------------------------------------------
    contact_email: state.contactEmail,
    company: state.company || "",
    job_title: state.jobTitle || "",
    gdpr_consent: !!state.gdprConsent,
    client_name: state.clientName,
    project_name: state.projectName,
    project_date: state.projectDate,
    submitted_at: new Date().toISOString(),
    page_url: typeof window !== "undefined" ? window.location.href : "",
    referrer: typeof document !== "undefined" ? document.referrer : "",
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",

    // --- Facility snapshot -----------------------------------------------
    luminaires: state.luminaires,
    existing_luminaires: state.existingLuminaires ?? state.luminaires,
    project_type: state.projectType || "renovation",
    burning_hours_per_week: state.burningHoursPerWeek,
    existing_wattage_w: state.existingWattage,
    proposed_wattage_w: state.proposedWattage,
    existing_runtime_pct: state.existingRuntimePct,
    upgrade_runtime_pct: state.upgradeRuntimePct,
    energy_price_per_kwh: state.pricePerKwh,
    currency: currencyCode,
    currency_symbol: symbol,

    // --- CapEx ------------------------------------------------------------
    installation_cost: state.installationCost,
    luminaire_cost: state.luminaireCost,
    compare_competitor: !!state.compareCompetitor,
    competitor_controls_cost: state.compareCompetitor
      ? state.competitorControlsCost
      : null,
    helvar_system_cost: state.helvarSystemCost,
    total_project_capex: results.totalProjectCapex,
    controls_premium_capex: results.controlsPremiumCapex,

    // --- Results ----------------------------------------------------------
    annual_kwh_a: Math.round(results.kwhA),
    annual_kwh_b: Math.round(results.kwhB),
    annual_kwh_c: Math.round(results.kwhC),
    annual_cost_a: Math.round(results.costA),
    annual_cost_b: Math.round(results.costB),
    annual_cost_c: Math.round(results.costC),
    saved_kwh_b_to_c: Math.round(results.savedB_to_C),
    financial_b_to_c: Math.round(results.financialB_to_C),
    co2_10yr_b_to_c_tonnes: Number(results.co2B_to_C.toFixed(2)),
    co2_10yr_a_to_c_tonnes: Number(results.co2A_to_C.toFixed(2)),
    payback_years: results.payback === null ? null : Number(results.payback.toFixed(2)),

    // --- Enabled upgrades (flat csv for easy reading in Google Sheets) ---
    enabled_upgrades: Object.entries(state.upgrades || {})
      .filter(([, v]) => v?.enabled)
      .map(([id, v]) => {
        const coverage = v.coveragePct;
        return coverage !== undefined && coverage !== null
          ? `${id}:${v.savingsPct}%@${coverage}%cov`
          : `${id}:${v.savingsPct}%`;
      })
      .join("; "),
    daylight_coverage_pct: state.upgrades?.daylight?.coveragePct ?? null,
    presence_coverage_pct: state.upgrades?.presence?.coveragePct ?? null,
    absence_coverage_pct: state.upgrades?.absence?.coveragePct ?? null,
    scheduling_coverage_pct: state.upgrades?.scheduling?.coveragePct ?? null,
    co2_kg_per_kwh: state.co2PerKwh,

    // --- Sales notes -----------------------------------------------------
    executive_summary: executiveSummary || "",

    // --- Attribution -----------------------------------------------------
    ...utm,
  };
}

export async function sendLead(args) {
  const webhookUrl = process.env.REACT_APP_LEAD_WEBHOOK_URL;
  if (!webhookUrl || webhookUrl.trim().length === 0) {
    // Not configured yet — silently skip so dev/preview still works.
    return { skipped: true };
  }

  const payload = buildPayload(args);

  try {
    // Zapier "Catch Hook" accepts a simple JSON POST.
    // We use no-cors to avoid CORS preflight issues with Zapier's hook URL,
    // which means we won't read the response — that's fine, Zapier always
    // returns 200 OK to the catch hook.
    await fetch(webhookUrl, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return { ok: true };
  } catch (err) {
    // Never let lead-capture failures block the PDF download.
    console.warn("Lead capture failed:", err);
    return { ok: false, error: err?.message };
  }
}
